import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SideBar from "../components/SideBar";
import "../styles/MemberDetails.css";
import { FaRegUserCircle } from "react-icons/fa";
import {
  MdOutlineAssignmentInd,
  MdOutlineLocationOn,
  MdUpdate,
  MdOutlineAdminPanelSettings,
  MdOutlinePublic,
} from "react-icons/md";
import DetailsHeaderBar from "../components/DetailsHeaderBar";
import DetailsTopBar from "../components/DetailsTopBar";
import DetailSection from "../components/DetailSection";
import { advertisingAgentAPI } from "../services/advertisingAgentService";

function AdvertisingAgentDetails() {
  const location = useLocation();
  const agent = location.state?.agent;
  const application = location.state?.application;
  const navigate = useNavigate();

  const [assignedAreas, setAssignedAreas] = useState([]);
  const [assignedFocusCountries, setAssignedFocusCountries] = useState([]);
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [loadingFocusCountries, setLoadingFocusCountries] = useState(false);
  const [showAreas, setShowAreas] = useState(false);
  const [showFocusCountries, setShowFocusCountries] = useState(false);
  const [focusCountryStats, setFocusCountryStats] = useState({
    total: 0,
    primary: 0,
    secondary: 0,
    target: 0
  });

  useEffect(() => {
    // Fetch assigned admin areas and focus countries if agent is Senior
    if (agent && agent.agent_type === "senior") {
      fetchAssignedAreas();
      fetchAssignedFocusCountries();
    }
  }, [agent]);

  const fetchAssignedAreas = async () => {
    setLoadingAreas(true);
    try {
      const response = await advertisingAgentAPI.getAgentAdminAreas(
        agent.agent_true_id
      );
      if (response.success) {
        setAssignedAreas(response.data.assigned_admin_areas || []);
      }
    } catch (error) {
      console.error("Error fetching assigned areas:", error);
    } finally {
      setLoadingAreas(false);
    }
  };

  const fetchAssignedFocusCountries = async () => {
    setLoadingFocusCountries(true);
    try {
      const response = await advertisingAgentAPI.getAgentFocusCountries(
        agent.agent_true_id
      );
      if (response.success) {
        setAssignedFocusCountries(response.data.focus_countries || []);
        
        // Update statistics
        if (response.data.statistics) {
          setFocusCountryStats(response.data.statistics);
        }
      }
    } catch (error) {
      console.error("Error fetching assigned focus countries:", error);
    } finally {
      setLoadingFocusCountries(false);
    }
  };

  // Helper function to get badge color based on focus type
  const getFocusTypeColor = (type) => {
    switch(type) {
      case "primary": return "#1976d2";
      case "secondary": return "#2e7d32";
      case "target": return "#ed6c02";
      default: return "#666";
    }
  };

  // Helper function to format focus type label
  const formatFocusType = (type) => {
    if (!type) return "N/A";
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  if (!agent) {
    return <p style={{ padding: "30px" }}>No agent data available.</p>;
  }

  // Extract data from the nested structure
  const firstName = agent.first_name || agent.firstName || "N/A";
  const surname = agent.last_name || agent.surname || "N/A";
  const email = agent.email || "N/A";
  const mobile = agent.phone_number || agent.mobile || "N/A";
  const applicationId = agent.application_id || agent.id || "N/A";
  
  console.log("applicationId:", applicationId);
  // Handle address data
  const addressLine1 =
    agent.address_line1 || (agent.address && agent.address.line1) || "N/A";
  const addressLine2 =
    agent.address_line2 || (agent.address && agent.address.line2) || "N/A";
  const addressLine3 =
    agent.address_line3 || (agent.address && agent.address.line3) || "N/A";
  const city = agent.city || (agent.address && agent.address.city) || "N/A";
  const region =
    agent.region || (agent.address && agent.address.county) || "N/A";
  const country =
    (agent.countries && agent.countries[0]?.name) ||
    (agent.address && agent.address.country) ||
    agent.country ||
    "N/A";
    const postcode =
    agent.postcode ||
    (agent.address && agent.address.postcode) ||
    "N/A";
    const referer_role = agent.referer_agent_type === 'mini' 
              ? 'Mini Customer Advertising Agent' 
              : agent.referer_agent_type === 'senior' 
              ? 'Senior Customer Advertising Agent' 
              : agent.referer_agent_type || 'N/A';
  // Handle status
  const status = agent.current_status || agent.status || "N/A";
  const notes = agent.status_note || agent.notes || "No notes available";
  const referrer = agent.referred_by || "N/A";

  // Determine agent type for display
  const agentType = agent.agent_type || application?.agent_type || "N/A";
  const isSeniorAgent = agentType === "senior";

  // Define all possible tabs
  const allTabs = [
    {
      key: "personal",
      label: "Personal Details",
      path: "/advertising-agent-details",
    },
    {
      key: "wallet",
      label: "Wallet Details",
      path: "/advertising-agent-wallet-details",
    },
    {
      key: "invitation",
      label: "Invitation Details",
      path: "/advertising-agent-invitation-details",
      showWhen: "approved",
    },
    {
      key: "request",
      label: "Promote / Demote",
      path: "/advertising-agent-request-history",
    },
  ];

  // Filter tabs based on status
  const getTabsByStatus = (agentStatus) => {
    return allTabs.filter((tab) => {
      if (!tab.showWhen) return true; // Always show tabs without conditions
      return tab.showWhen === agentStatus;
    });
  };

  // Usage
  const tabs = getTabsByStatus(agent.status);
  
  const goToEdit = () => {
     navigate("/advertising-agent-edit", { state: { agent } });
  };

  const toggleAreas = () => {
    setShowAreas(!showAreas);
  };

  const toggleFocusCountries = () => {
    setShowFocusCountries(!showFocusCountries);
  };

  return (
    <div className="member-details-layout">
      <SideBar />
      <div className="member-details-main">
        <DetailsHeaderBar
          title="Advertising Agent Details"
          subtitle="You can edit your details to update your profile"
          breadcrumbs={[
            { label: "Home", path: "/dashboard" },
            {
              label: "Manage Customer Advertising Agent",
              path: "/manage-advertising-agent",
            },
            { label: "Advertising Agent Details" },
          ]}
        />
        <DetailsTopBar tabs={tabs} activeTab="personal" agent={agent} />

        {/* Basic details */}
        <DetailSection
          title="Basic Details"
          icon={FaRegUserCircle}
          showEdit={true}
          onEdit={goToEdit}
          fields={[
            { label: "First name", value: firstName },
            { label: "Surname", value: surname },
            { label: "Email", value: email },
            { label: "Mobile", value: mobile },
            { label: "Agent Type", value: isSeniorAgent ? "Senior Advertising Agent" : "Mini Advertising Agent" },
            /* { label: "Application ID", value: applicationId } */
          ]}
        />

        {/* Address */}
        <DetailSection
          title="Address"
          icon={MdOutlineLocationOn}
          fields={[
            { label: "Address line 1", value: addressLine1 },
            { label: "Address line 2", value: addressLine2 },
            { label: "Address line 3", value: addressLine3 },
            { label: "City", value: city },
            { label: "Covering region", value: region },
            { label: "Postcode", value: postcode },
            { label: "Country", value: country },
          ]}
        />

        {/* Status */}
        <DetailSection
          title="Status"
          icon={MdUpdate}
          fields={[
            { label: "Email Status", value: status },
            { label: "Notes", value: notes },
          ]}
        />

        {/* Referred by */}
        <DetailSection
          title="Referred By"
          icon={MdOutlineAssignmentInd}
          fields={[
            { label: "Referrer", value: referrer || "N/A" },
            { label: "Referrer ID", value: agent.referer_id || 'N/A' },
            { label: "Referrer Agent Type", value: referer_role || 'N/A' },
            { label: "Referrer Country", value: agent.referer_country || 'N/A' },
            { label: "Referrer Region", value: agent.referer_region || 'N/A' },
            { label: "Referrer Email", value: agent.referer_email || 'N/A' }
          ]}
        />

        {/* Admin Areas - Only for Senior Agents */}
        {isSeniorAgent && (
          <div className="section">
            <h3>
              <MdOutlineAdminPanelSettings /> Admin Areas
              {assignedAreas.length > 0 && (
                <button
                  onClick={toggleAreas}
                  style={{
                    marginLeft: "10px",
                    background: "none",
                    border: "1px solid rgba(0, 0, 160, 1)",
                    borderRadius: "15px",
                    padding: "2px 10px",
                    fontSize: "12px",
                    cursor: "pointer",
                    color: "rgba(0, 0, 160, 1)",
                  }}
                >
                  {showAreas ? "Hide" : `View (${assignedAreas.length})`}
                </button>
              )}
            </h3>
            <div className="section-content">
              {loadingAreas ? (
                <p>Loading assigned areas...</p>
              ) : assignedAreas.length === 0 ? (
                <p>
                  <strong>No admin areas assigned</strong>
                </p>
              ) : showAreas ? (
                <div className="admin-areas-list">
                  {assignedAreas.map((area, index) => (
                    <div
                      key={area.admin_area_id || index}
                      style={{
                        padding: "8px",
                        margin: "5px 0",
                        background: "#f5f5f5",
                        borderRadius: "4px",
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <p style={{ margin: "2px 0" }}>
                        <strong>{area.admin_name}</strong>
                        {area.county && (
                          <span style={{ color: "#666", marginLeft: "8px" }}>
                            ({area.county.county_name || area.county})
                          </span>
                        )}
                      </p>
                      <p
                        style={{
                          margin: "2px 0",
                          fontSize: "12px",
                          color: "#888",
                        }}
                      >
                        Towns: {area.towns_count || "N/A"} | Assigned:{" "}
                        {area.assigned_at ? new Date(area.assigned_at).toLocaleDateString() : "N/A"}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p>
                  <strong>{assignedAreas.length} admin area(s) assigned</strong>
                </p>
              )}
            </div>
          </div>
        )}

        {/* Focus Countries - Only for Senior Agents */}
        {isSeniorAgent && (
          <div className="section">
            <h3>
              <MdOutlinePublic /> Focus Countries
              {assignedFocusCountries.length > 0 && (
                <button
                  onClick={toggleFocusCountries}
                  style={{
                    marginLeft: "10px",
                    background: "none",
                    border: "1px solid rgba(0, 0, 160, 1)",
                    borderRadius: "15px",
                    padding: "2px 10px",
                    fontSize: "12px",
                    cursor: "pointer",
                    color: "rgba(0, 0, 160, 1)",
                  }}
                >
                  {showFocusCountries ? "Hide" : `View (${assignedFocusCountries.length})`}
                </button>
              )}
            </h3>

            {/* Focus Country Statistics Summary */}
            {assignedFocusCountries.length > 0 && (
              <div
                style={{
                  marginBottom: "15px",
                  background: "#e8f4fd",
                  padding: "10px 15px",
                  borderRadius: "6px",
                  display: "flex",
                  gap: "20px",
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <strong>Total:</strong> {focusCountryStats.total}
                </div>
                <div style={{ color: "#1976d2" }}>
                  <strong>Primary:</strong> {focusCountryStats.primary}
                </div>
                <div style={{ color: "#2e7d32" }}>
                  <strong>Secondary:</strong> {focusCountryStats.secondary}
                </div>
                <div style={{ color: "#ed6c02" }}>
                  <strong>Target:</strong> {focusCountryStats.target}
                </div>
              </div>
            )}

            <div className="section-content">
              {loadingFocusCountries ? (
                <p>Loading assigned focus countries...</p>
              ) : assignedFocusCountries.length === 0 ? (
                <p>
                  <strong>No focus countries assigned</strong>
                </p>
              ) : showFocusCountries ? (
                <div className="focus-countries-list">
                  {assignedFocusCountries.map((country, index) => (
                    <div
                      key={country.country_id || index}
                      style={{
                        padding: "12px",
                        margin: "8px 0",
                        background: "#f5f5f5",
                        borderRadius: "6px",
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "10px" }}>
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", marginBottom: "5px" }}>
                            <strong style={{ fontSize: "16px" }}>{country.country_name}</strong>
                            <span style={{
                              background: getFocusTypeColor(country.focus_type),
                              color: "white",
                              padding: "2px 8px",
                              borderRadius: "12px",
                              fontSize: "12px"
                            }}>
                              {formatFocusType(country.focus_type)}
                            </span>
                          </div>
                          
                          {country.notes && (
                            <div style={{ 
                              fontSize: "13px", 
                              color: "#666", 
                              marginTop: "4px",
                              background: "#fff",
                              padding: "6px 10px",
                              borderRadius: "4px",
                              border: "1px dashed #ccc"
                            }}>
                              <em>Note: {country.notes}</em>
                            </div>
                          )}
                          
                          <div style={{
                            fontSize: "11px",
                            color: "#888",
                            marginTop: "8px",
                            display: "flex",
                            gap: "15px",
                            flexWrap: "wrap"
                          }}>
                            <span>Assigned: {country.assigned_at ? new Date(country.assigned_at).toLocaleDateString() : "N/A"}</span>
                            <span>Updated: {country.updated_at ? new Date(country.updated_at).toLocaleDateString() : "N/A"}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p>
                  <strong>{assignedFocusCountries.length} focus countr{assignedFocusCountries.length === 1 ? 'y' : 'ies'} assigned</strong>
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdvertisingAgentDetails;